import React, {
  useEffect,
  useRef,
  useImperativeHandle,
  forwardRef,
  useState,
  useCallback,
} from "react";
import WaveSurfer from "wavesurfer.js";
import { FaExclamationCircle } from "react-icons/fa";
import styled from "styled-components";
import { theme } from "@src/theme";
import { useAppDispatch } from "@src/redux/hooks";
import {
  setCurrentPlayingProcessedPlaylist,
  setCurrentPlayingTrack,
  setIsPlaying,
  setTrackStatus,
} from "@src/redux/slices/trackPlayerSlice";
import { WabData, fetchWab, sampleWabMonomix } from "./wab";

/** Exact background the waveform is drawn on, in image and in binary mode. */
const WAVEFORM_BACKGROUND = "#2d2d2d";

/**
 * Horizontal padding the desktop app leaves on each side of the waveform PNG
 * (`PADDING_H` in `WaveFormImageExtractor`). The peaks only occupy
 * `naturalWidth - 2 * PADDING_H` pixels, so the image is scaled up by
 * `naturalWidth / (naturalWidth - 88)` to make the drawn region — and only it —
 * fill the container. That is what keeps the cursor, which is positioned as a
 * percentage of the container, on top of the peak for that moment.
 *
 * The image used to be 1000px wide for every track, but since ROVR-3809 its
 * width scales with duration (1000..9000), so the factor has to be computed per
 * image rather than hardcoded.
 */
const WAVEFORM_IMAGE_PADDING_H = 44;
const DEFAULT_IMAGE_WIDTH = 1000;
const imageScaleForWidth = (naturalWidth: number): number =>
  naturalWidth > WAVEFORM_IMAGE_PADDING_H * 2
    ? naturalWidth / (naturalWidth - WAVEFORM_IMAGE_PADDING_H * 2)
    : 1;

/**
 * Vertical gain applied to the image. Geometry alone would call for
 * `500 / (500 - 2 * 22)` = 1.0965; 1.3 is a deliberate exaggeration so quiet
 * mixes still read as a waveform at 50px tall, at the cost of flattening
 * anything above ~0.84 of full scale.
 */
const IMAGE_VERTICAL_GAIN = 1.3;

/**
 * Seconds between what the media clock reports and what you actually hear.
 * `currentTime` is the position of the decode pipeline; the samples after it
 * are still queued in the output path — a few milliseconds on wired output,
 * but 150-500ms over Bluetooth, which is enough to see the cursor arrive at a
 * peak before the sound does.
 *
 * The browser measures this for the current output device, so compensating with
 * it costs nothing on wired setups (the value is ~0) instead of applying a
 * constant that would be wrong on half of them. Safari does not implement
 * `outputLatency`; there the correction is simply not applied.
 */
let sharedAudioContext: AudioContext | null = null;
const outputLatencySeconds = (): number => {
  try {
    const AudioContextCtor =
      window.AudioContext || (window as any).webkitAudioContext;
    if (!AudioContextCtor) return 0;

    if (!sharedAudioContext) sharedAudioContext = new AudioContextCtor();
    const context = sharedAudioContext as AudioContext & {
      outputLatency?: number;
    };
    const latency = context.outputLatency ?? context.baseLatency ?? 0;
    // A device reporting more than a second is reporting nonsense
    return Number.isFinite(latency) ? Math.min(Math.max(latency, 0), 1) : 0;
  } catch {
    return 0;
  }
};

/**
 * Development escape hatch: `?wab=<url>` renders that binary waveform on any
 * playlist. Only playlists reprocessed by a desktop app build that ships
 * ROVR-3809 have a `waveformBinary` of their own, so without this the renderer
 * cannot be looked at until enough of them exist. Ignored outside `npm start`.
 */
const devWaveformBinaryOverride = (): string | undefined => {
  if (process.env.NODE_ENV !== "development") return undefined;
  try {
    return new URLSearchParams(window.location.search).get("wab") || undefined;
  } catch {
    return undefined;
  }
};

/** Fraction of the half-height the tallest peak occupies, as in the reference renderer. */
const WAB_VERTICAL_SPAN = 0.9;
/** Peaks below this are silence and are not drawn at all. */
const WAB_EPSILON = 0.0001;

const WaveformOuterContainer = styled.div<{ $isScrollable: boolean }>`
  width: 95%;
  height: 50px;
  overflow-x: ${(props) => (props.$isScrollable ? "auto" : "hidden")};
  overflow-y: hidden;

  /* Scrollbar styles for image mode zoom */
  &::-webkit-scrollbar {
    height: 4px;
  }
  &::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.1);
  }
  &::-webkit-scrollbar-thumb {
    background: ${theme.colors.yellow};
    border-radius: 2px;
  }
`;

const WaveformWrapper = styled.div`
  width: 100%;
  height: 50px;
`;

const WaveformImageContainer = styled.div<{ $width: string }>`
  position: relative;
  width: ${(props) => props.$width};
  height: 50px;
  cursor: pointer;
  overflow: hidden;
  border-radius: 2px;
  background-color: ${WAVEFORM_BACKGROUND};
`;

const WaveformImg = styled.img<{ $scaleX: number }>`
  width: 100%;
  height: 100%;
  object-fit: fill;
  display: block;
  user-select: none;
  pointer-events: none;

  /* Prevent the browser from blurring the image when stretched or zoomed */
  image-rendering: pixelated;
  image-rendering: crisp-edges;

  /* Filter to convert blue-on-white waveform to exact peak color intensity */
  /* brightness(0.59) gives roughly the color #979794 from a pure white source */
  filter: invert(1) grayscale(1) brightness(0.59);

  /* Blend mode to make the black background transparent over the #2D2D2D container */
  mix-blend-mode: lighten;

  /* Crops the generator's padding so peaks line up with the cursor. Scaling
     (rather than cropping with a background-position) keeps that alignment at
     every zoom level. */
  transform: ${(props) => `scale(${props.$scaleX}, ${IMAGE_VERTICAL_GAIN})`};
  transform-origin: center center;
`;

const WaveformPlayedOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  width: 0%;
  background-color: ${theme.colors.yellow};
  mix-blend-mode: screen; /* Changed to screen for better blending on dark inverted background */
  opacity: 0.4;
  pointer-events: none;
  transition: none;
`;

const WaveformCursor = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0%;
  width: 2px;
  background-color: ${theme.colors.yellow};
  pointer-events: none;
`;

/**
 * The binary waveform is painted on a canvas the size of the *visible* area,
 * kept on top of the scroller, while an empty spacer inside the scroller
 * provides the zoomed width. A single canvas as wide as the zoomed waveform
 * would blow past the browser's 32k pixel canvas limit on a long mix.
 */
const WabViewport = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  justify-content: center;
`;

const WabCanvas = styled.canvas`
  position: absolute;
  top: 0;
  /* The scroller is 95% wide and centred, and the canvas is sized to match its
     client width, so centring is what keeps the two boxes aligned. */
  left: 50%;
  transform: translateX(-50%);
  pointer-events: none;
  border-radius: 2px;
`;

const WabSpacer = styled.div<{ $width: string }>`
  width: ${(props) => props.$width};
  height: 50px;
  cursor: pointer;
`;

/**
 * Shown in place of the waveform when the browser cannot play the track's audio
 * (unsupported codec/container, a decode failure, etc.). Matches the waveform
 * strip's footprint so the player's layout does not shift.
 */
const WaveformUnplayableMessage = styled.div`
  width: 95%;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 0 12px;
  border-radius: 2px;
  background-color: ${WAVEFORM_BACKGROUND};
  color: ${theme.colors.gray100};
  font-family: "Kern Standard", Arial, sans-serif;
  font-size: 12px;
  text-align: center;

  svg {
    flex-shrink: 0;
    color: ${theme.colors.yellow};
  }
`;

const UnplayableIcon = FaExclamationCircle as unknown as React.FC;

interface AudioWaveformProps {
  trackURL: string;
  startPlaying: boolean;
  peaks?: (Float32Array | number[])[];
  waveformImageUrl?: string;
  /**
   * Url of the compact `.wab` binary waveform. When it loads it replaces the
   * image; if it fails (missing file, CORS, older format) the image is used, so
   * callers should keep passing `waveformImageUrl` as well.
   */
  waveformBinaryUrl?: string;
  onReady?: (durationSeconds: number) => void;
  onTimeUpdate?: (currentSeconds: number, durationSeconds: number) => void;
  onUserScroll?: () => void;
}

export interface AudioWaveformRef {
  playAudio: () => void;
  pauseAudio: () => void;
  resetWaveform: () => void;
  toggleAudio: () => void;
  isPlaying: () => boolean;
  getDuration: () => number;
  getCurrentTime: () => number;
  zoom: (pxPerSec: number) => void;
  setAutoScroll: (enabled: boolean) => void;
  scrollToCurrentTime: () => void;
}

const AudioWaveform = forwardRef<AudioWaveformRef, AudioWaveformProps>(
  (
    {
      trackURL,
      startPlaying = false,
      peaks = [],
      waveformImageUrl,
      waveformBinaryUrl: waveformBinaryUrlProp,
      onReady,
      onTimeUpdate,
      onUserScroll,
    },
    ref
  ) => {
    const waveformBinaryUrl =
      devWaveformBinaryOverride() ?? waveformBinaryUrlProp;
    const waveformRef = useRef<HTMLDivElement>(null);
    const outerContainerRef = useRef<HTMLDivElement>(null);
    const waveSurferRef = useRef<WaveSurfer | null>(null);
    const dispatch = useAppDispatch();

    const [unplayable, setUnplayable] = useState(false);
    const [imageWidth, setImageWidth] = useState<string>("100%");
    const [imageScaleX, setImageScaleX] = useState<number>(
      imageScaleForWidth(DEFAULT_IMAGE_WIDTH)
    );
    const durationRef = useRef<number>(0);
    const autoScrollRef = useRef<boolean>(true);

    // Refs for direct DOM updates on waveform image overlay (no re-renders)
    const playedOverlayRef = useRef<HTMLDivElement>(null);
    const cursorRef = useRef<HTMLDivElement>(null);

    // Binary waveform state
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const wabRef = useRef<WabData | null>(null);
    const [wabLoaded, setWabLoaded] = useState(false);
    const progressRatioRef = useRef<number>(0);
    const drawRequestRef = useRef<number | null>(null);

    /**
     * Whether WaveSurfer's own canvas is suppressed in favour of the image or
     * the binary waveform. Decided from the props rather than from load state so
     * that a failed `.wab` download cannot leave WaveSurfer half-configured.
     */
    const rendersOwnWaveform = !!waveformImageUrl || !!waveformBinaryUrl;
    const rendersOwnWaveformRef = useRef(rendersOwnWaveform);
    rendersOwnWaveformRef.current = rendersOwnWaveform;

    const showsWab = !!waveformBinaryUrl && wabLoaded;
    const showsImage = !showsWab && !!waveformImageUrl;

    const resetState = () => {
      dispatch(setCurrentPlayingTrack(null));
      dispatch(setCurrentPlayingProcessedPlaylist(null));
      dispatch(setTrackStatus("idle"));
      dispatch(setIsPlaying(false));
    };

    const drawWab = useCallback(() => {
      const canvas = canvasRef.current;
      const scroller = outerContainerRef.current;
      const data = wabRef.current;
      if (!canvas || !scroller || !data) return;

      const width = scroller.clientWidth;
      const height = scroller.clientHeight;
      if (!width || !height) return;

      const dpr = window.devicePixelRatio || 1;
      const pixelWidth = Math.round(width * dpr);
      const pixelHeight = Math.round(height * dpr);
      if (canvas.width !== pixelWidth || canvas.height !== pixelHeight) {
        canvas.width = pixelWidth;
        canvas.height = pixelHeight;
      }
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;

      const ctx = canvas.getContext("2d");
      if (!ctx) return;

      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.fillStyle = WAVEFORM_BACKGROUND;
      ctx.fillRect(0, 0, width, height);

      const virtualWidth = Math.max(scroller.scrollWidth, width);
      const scrollLeft = scroller.scrollLeft;
      const centerY = height / 2;
      const span = centerY * WAB_VERTICAL_SPAN;
      const progressX = progressRatioRef.current * virtualWidth - scrollLeft;

      for (let i = 0; i < width; i += 1) {
        const x = scrollLeft + i;
        const value = sampleWabMonomix(
          data,
          x / virtualWidth,
          (x + 1) / virtualWidth
        );
        if (value <= WAB_EPSILON) continue;

        const half = value * span;
        ctx.fillStyle =
          i <= progressX ? theme.colors.yellow : theme.colors.gray100;
        ctx.fillRect(i, centerY - half, 1, Math.max(half * 2, 1));
      }

      if (progressX >= 0 && progressX <= width) {
        ctx.fillStyle = theme.colors.yellow;
        ctx.fillRect(Math.min(progressX, width - 2), 0, 2, height);
      }
    }, []);

    const scheduleWabDraw = useCallback(() => {
      if (drawRequestRef.current !== null) return;
      drawRequestRef.current = window.requestAnimationFrame(() => {
        drawRequestRef.current = null;
        drawWab();
      });
    }, [drawWab]);

    /**
     * Horizontal position of a playback time, as a fraction of the drawn
     * waveform.
     *
     * The waveform spans the duration of the audio the *generator* decoded,
     * which is not necessarily the duration of the audio we are streaming — a
     * mix rendered with a lead-in, or trimmed on upload, leaves the two a second
     * or two apart. Dividing by the player's duration in that case puts the
     * cursor over the peak before (or after) you hear it, drifting further the
     * longer the mix runs. The binary waveform carries its own duration, so it
     * is the reference whenever we have it.
     */
    const toWaveformRatio = useCallback(
      (currentSeconds: number, audioDuration: number) => {
        const waveformDuration = wabRef.current?.durationSeconds ?? 0;
        const reference =
          waveformDuration > 0 ? waveformDuration : audioDuration;
        if (!reference) return 0;
        return Math.max(0, Math.min(1, currentSeconds / reference));
      },
      []
    );

    /** Moves the cursor of whichever renderer is active. Safe to call in any mode. */
    const applyProgress = useCallback(
      (currentSeconds: number, audioDuration: number) => {
        const ratio = toWaveformRatio(currentSeconds, audioDuration);
        progressRatioRef.current = ratio;
        const percent = `${ratio * 100}%`;
        if (playedOverlayRef.current)
          playedOverlayRef.current.style.width = percent;
        if (cursorRef.current) cursorRef.current.style.left = percent;
        scheduleWabDraw();
      },
      [scheduleWabDraw, toWaveformRatio]
    );

    /**
     * Reads the media clock on every animation frame while playing, so the
     * cursor follows the audio continuously instead of stepping forward with
     * `timeupdate`. Only touches refs and the canvas, never React state.
     */
    const playheadFrameRef = useRef<number | null>(null);
    const outputLatencyRef = useRef<number>(0);

    /** Position to draw the cursor at: what is audible now, not what is decoded. */
    const audibleTime = useCallback(
      (ws: WaveSurfer) =>
        Math.max(0, ws.getCurrentTime() - outputLatencyRef.current),
      []
    );

    const stopPlayhead = useCallback(() => {
      if (playheadFrameRef.current !== null) {
        window.cancelAnimationFrame(playheadFrameRef.current);
        playheadFrameRef.current = null;
      }
    }, []);

    const startPlayhead = useCallback(() => {
      if (playheadFrameRef.current !== null) return;

      // Re-read on every play: the user can switch output device mid-session
      outputLatencyRef.current = outputLatencySeconds();

      const tick = () => {
        playheadFrameRef.current = null;

        const ws = waveSurferRef.current;
        if (!ws) return;

        const duration = ws.getDuration();
        if (duration > 0 && rendersOwnWaveformRef.current) {
          applyProgress(audibleTime(ws), duration);

          if (autoScrollRef.current && outerContainerRef.current) {
            const { scrollWidth, clientWidth } = outerContainerRef.current;
            if (scrollWidth > clientWidth) {
              outerContainerRef.current.scrollLeft = Math.max(
                0,
                progressRatioRef.current * scrollWidth - clientWidth / 2
              );
            }
          }
        }

        playheadFrameRef.current = window.requestAnimationFrame(tick);
      };

      playheadFrameRef.current = window.requestAnimationFrame(tick);
    }, [applyProgress]);

    // Download the binary waveform. A failure is not fatal: the image (or
    // WaveSurfer's own peaks) stays on screen.
    useEffect(() => {
      wabRef.current = null;
      setWabLoaded(false);
      if (!waveformBinaryUrl) return undefined;

      const abortController = new AbortController();
      let cancelled = false;

      fetchWab(waveformBinaryUrl, abortController.signal)
        .then((data) => {
          if (cancelled) return;
          wabRef.current = data;
          setWabLoaded(true);

          // A mismatch here is what makes the cursor run ahead of (or behind)
          // what you hear, so surface it rather than silently compensating.
          const audioDuration = durationRef.current;
          const drift = Math.abs(data.durationSeconds - audioDuration);
          if (audioDuration > 0 && drift > 0.5) {
            // eslint-disable-next-line no-console
            console.warn(
              `Waveform covers ${data.durationSeconds.toFixed(2)}s but the audio is ${audioDuration.toFixed(2)}s ` +
                `(${drift.toFixed(2)}s apart). Positioning against the waveform's own duration.`
            );
          }
        })
        .catch((error) => {
          if (cancelled || abortController.signal.aborted) return;
          // eslint-disable-next-line no-console
          console.warn(
            `Falling back to the waveform image, could not load ${waveformBinaryUrl}:`,
            error
          );
        });

      return () => {
        cancelled = true;
        abortController.abort();
      };
    }, [waveformBinaryUrl]);

    // Repaint on load, on zoom, and whenever the viewport changes size.
    useEffect(() => {
      if (!showsWab) return undefined;
      scheduleWabDraw();

      const scroller = outerContainerRef.current;
      if (!scroller) return undefined;

      const handleScroll = () => scheduleWabDraw();
      scroller.addEventListener("scroll", handleScroll, { passive: true });

      const observer = new ResizeObserver(() => scheduleWabDraw());
      observer.observe(scroller);

      return () => {
        scroller.removeEventListener("scroll", handleScroll);
        observer.disconnect();
      };
    }, [showsWab, imageWidth, scheduleWabDraw]);

    useEffect(
      () => () => {
        if (drawRequestRef.current !== null) {
          window.cancelAnimationFrame(drawRequestRef.current);
        }
      },
      []
    );

    const doScrollToCurrentTime = () => {
      try {
        const ws = waveSurferRef.current;
        if (!ws) return;
        const duration = ws.getDuration();
        if (!duration) return;
        const progress = ws.getCurrentTime() / duration;

        if (rendersOwnWaveformRef.current && outerContainerRef.current) {
          // Scroll the image / binary waveform container, in waveform space so
          // the centred position matches where the cursor is drawn
          const waveformProgress = toWaveformRatio(
            ws.getCurrentTime(),
            duration
          );
          const { scrollWidth, clientWidth } = outerContainerRef.current;
          const target = Math.max(
            0,
            waveformProgress * scrollWidth - clientWidth / 2
          );
          outerContainerRef.current.scrollLeft = target;
        } else {
          // Scroll WaveSurfer
          const renderer = (ws as any).renderer;
          if (!renderer?.scrollContainer) return;
          const { scrollWidth, clientWidth } = renderer.scrollContainer;
          const target = Math.max(0, progress * scrollWidth - clientWidth / 2);
          renderer.scrollContainer.scrollLeft = target;
        }
      } catch {}
    };

    useImperativeHandle(ref, () => ({
      playAudio: () => {
        waveSurferRef.current?.play();
        dispatch(setIsPlaying(true));
        dispatch(setTrackStatus("playing"));
      },
      pauseAudio: () => {
        waveSurferRef.current?.pause();
        dispatch(setIsPlaying(false));
        dispatch(setTrackStatus("paused"));
      },
      resetWaveform: () => {
        waveSurferRef.current?.stop();
        dispatch(setIsPlaying(false));
        dispatch(setTrackStatus("idle"));
        resetState();
      },
      toggleAudio: () => {
        if (waveSurferRef.current?.isPlaying()) {
          waveSurferRef.current.pause();
          dispatch(setIsPlaying(false));
          dispatch(setTrackStatus("paused"));
        } else {
          waveSurferRef.current?.play();
          dispatch(setIsPlaying(true));
          dispatch(setTrackStatus("playing"));
        }
      },
      isPlaying: () => waveSurferRef.current?.isPlaying() || false,
      getDuration: () => waveSurferRef.current?.getDuration() || 0,
      getCurrentTime: () => waveSurferRef.current?.getCurrentTime() || 0,
      zoom: (pxPerSec: number) => {
        try {
          waveSurferRef.current?.zoom(pxPerSec);
        } catch {}

        // Handle image / binary waveform zoom
        if (rendersOwnWaveformRef.current && durationRef.current > 0) {
          if (pxPerSec <= 0) {
            setImageWidth("100%");
          } else {
            const calculatedWidth = durationRef.current * pxPerSec;
            // Only apply if it's larger than the container width (approx 100%)
            setImageWidth(`${calculatedWidth}px`);
          }
        }

        // Always center on the playback indicator after zoom
        setTimeout(() => {
          doScrollToCurrentTime();
        }, 50); // Small timeout to let the DOM update its width after zoom
      },
      setAutoScroll: (enabled: boolean) => {
        autoScrollRef.current = enabled;
        try {
          waveSurferRef.current?.setOptions({ autoScroll: enabled });
        } catch {}
      },
      scrollToCurrentTime: () => {
        doScrollToCurrentTime();
      },
    }));

    // Detect user-initiated scroll (wheel events bubble out of WaveSurfer's shadow DOM)
    const onUserScrollRef = useRef(onUserScroll);
    onUserScrollRef.current = onUserScroll;
    const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
      const el = outerContainerRef.current;
      if (!el) return;

      const handler = () => {
        onUserScrollRef.current?.();

        // Disable auto-scroll while user is scrolling
        autoScrollRef.current = false;
        try {
          waveSurferRef.current?.setOptions({ autoScroll: false });
        } catch {}

        // Clear previous timeout
        if (scrollTimeoutRef.current) {
          clearTimeout(scrollTimeoutRef.current);
        }

        // Set a new timeout to resume auto-scroll after 3 seconds of inactivity
        scrollTimeoutRef.current = setTimeout(() => {
          autoScrollRef.current = true;
          try {
            waveSurferRef.current?.setOptions({ autoScroll: true });
          } catch {}
          doScrollToCurrentTime();
        }, 3000);
      };

      el.addEventListener("wheel", handler, { passive: true });
      return () => {
        el.removeEventListener("wheel", handler);
        if (scrollTimeoutRef.current) {
          clearTimeout(scrollTimeoutRef.current);
        }
      };
    }, []);

    useEffect(() => {
      if (!waveformRef.current) return;

      const abortController = new AbortController();
      const customMediaElement = document.createElement("audio");
      customMediaElement.crossOrigin = "anonymous";
      customMediaElement.preload = "metadata";
      customMediaElement.src = trackURL;

      // New source: clear any previous unplayable state before it loads.
      setUnplayable(false);

      // Whether the element is being torn down. Clearing `src` in cleanup fires
      // an `error` (MEDIA_ERR_ABORTED / empty-src), which must not be mistaken
      // for a genuinely unplayable track.
      let destroyed = false;

      // The browser cannot decode/play this source (unsupported codec or
      // container, a decode failure, an unreachable file). Surface it in the
      // player instead of sitting on "loading" forever.
      const handleMediaError = () => {
        if (destroyed) return;
        const mediaError = customMediaElement.error;
        if (!mediaError || mediaError.code === MediaError.MEDIA_ERR_ABORTED) {
          return;
        }
        stopPlayhead();
        setUnplayable(true);
        dispatch(setTrackStatus("error"));
        dispatch(setIsPlaying(false));
      };
      customMediaElement.addEventListener("error", handleMediaError);

      const effectivePeaks = rendersOwnWaveform
        ? [[0, 0]]
        : peaks.length > 0
          ? peaks
          : undefined;

      waveSurferRef.current = WaveSurfer.create({
        sampleRate: 44100,
        container: waveformRef.current,
        waveColor: theme.colors.gray100,
        progressColor: theme.colors.yellow,
        cursorColor: theme.colors.yellow,
        barWidth: 0.5,
        barRadius: 0,
        barGap: 0,
        height: rendersOwnWaveform ? 0 : 55, // hide WaveSurfer canvas when we draw it ourselves
        cursorWidth: 2,
        normalize: true,
        media: customMediaElement,
        peaks: effectivePeaks,
        dragToSeek: !rendersOwnWaveform, // our own container handles seek instead
        fetchParams: {
          signal: abortController.signal,
        },
      });

      dispatch(setTrackStatus("loading"));

      const fireReadyFromAudio = () => {
        const dur =
          waveSurferRef.current?.getDuration() ||
          customMediaElement.duration ||
          0;
        if (dur) {
          durationRef.current = dur;
          onReady?.(dur);
        }
      };
      customMediaElement.addEventListener("loadedmetadata", fireReadyFromAudio);
      customMediaElement.addEventListener("durationchange", fireReadyFromAudio);

      waveSurferRef.current.on("ready", () => {
        const dur = waveSurferRef.current?.getDuration() || 0;
        if (dur) {
          durationRef.current = dur;
          onReady?.(dur);
        }
      });

      // `audioprocess` rides on the media element's `timeupdate`, which browsers
      // only fire about four times a second. That is fine for the timecode
      // (which is React state, and re-rendering it 60 times a second would be
      // wasteful) but not for the cursor: at 250ms per step, zoomed in far
      // enough that a step spans real distance, the cursor reads as lagging
      // behind what you hear. The playhead below runs on its own frame loop.
      waveSurferRef.current.on("audioprocess", (current: number) => {
        const dur = waveSurferRef.current?.getDuration() || 0;
        onTimeUpdate?.(current || 0, dur);
      });

      customMediaElement.addEventListener("play", startPlayhead);
      customMediaElement.addEventListener("pause", stopPlayhead);
      customMediaElement.addEventListener("ended", stopPlayhead);

      waveSurferRef.current.on("seeking", () => {
        const ws = waveSurferRef.current;
        if (!ws) return;

        const current = ws.getCurrentTime() || 0;
        const dur = ws.getDuration() || 0;
        onTimeUpdate?.(current, dur);
        // Same compensation as the playhead loop, so pressing play does not
        // make the cursor jump backwards by the output latency
        if (rendersOwnWaveformRef.current && dur > 0) {
          applyProgress(audibleTime(ws), dur);
        }
      });

      customMediaElement.addEventListener("canplay", () => {
        dispatch(setTrackStatus("ready"));

        if (startPlaying || waveSurferRef.current?.isPlaying()) {
          customMediaElement?.play().then(() => {
            dispatch(setTrackStatus("playing"));
            dispatch(setIsPlaying(true));
          });
        }
      });
      customMediaElement.load();

      const destroyRef = () => {
        if (waveSurferRef.current) {
          try {
            waveSurferRef.current.unAll();
            waveSurferRef.current.destroy();
          } catch {}
          waveSurferRef.current = null;
        }
      };

      waveSurferRef.current?.on("pause", () => {
        dispatch(setIsPlaying(false));
        dispatch(setTrackStatus("paused"));
      });

      return () => {
        destroyed = true;
        stopPlayhead();
        destroyRef();
        if (customMediaElement) {
          customMediaElement.removeEventListener("error", handleMediaError);
          customMediaElement.pause();
          customMediaElement.src = "";
          customMediaElement.load();
          customMediaElement.remove();
        }
      };
    }, [trackURL]);

    /**
     * Seeks from a click on an element that spans the whole (zoomed) waveform,
     * so the element's own box gives the ratio without reading scroll offsets.
     */
    const handleSeekClick = (e: React.MouseEvent<HTMLDivElement>) => {
      const ws = waveSurferRef.current;
      if (!ws) return;

      const rect = e.currentTarget.getBoundingClientRect();
      const waveformRatio = Math.max(
        0,
        Math.min(1, (e.clientX - rect.left) / rect.width)
      );

      // The click lands on a position of the *waveform*, so read the time there
      // and convert to a position of the audio, which may not be the same length
      const audioDuration = ws.getDuration();
      const waveformDuration = wabRef.current?.durationSeconds ?? 0;
      if (!waveformDuration || !audioDuration) {
        ws.seekTo(waveformRatio);
        return;
      }

      const seconds = waveformRatio * waveformDuration;
      ws.seekTo(Math.max(0, Math.min(1, seconds / audioDuration)));
    };

    const handleImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
      const { naturalWidth } = e.currentTarget;
      if (naturalWidth) setImageScaleX(imageScaleForWidth(naturalWidth));
    };

    const scroller = (
      <WaveformOuterContainer
        ref={outerContainerRef}
        $isScrollable={rendersOwnWaveform}
      >
        <WaveformWrapper
          ref={waveformRef}
          style={
            rendersOwnWaveform ? { height: 0, overflow: "hidden" } : undefined
          }
        />
        {showsWab && (
          <WabSpacer $width={imageWidth} onClick={handleSeekClick} />
        )}
        {showsImage && (
          <WaveformImageContainer $width={imageWidth} onClick={handleSeekClick}>
            <WaveformImg
              src={waveformImageUrl}
              alt="Playlist waveform"
              onLoad={handleImageLoad}
              $scaleX={imageScaleX}
            />
            <WaveformPlayedOverlay ref={playedOverlayRef} />
            <WaveformCursor ref={cursorRef} />
          </WaveformImageContainer>
        )}
      </WaveformOuterContainer>
    );

    if (unplayable) {
      return (
        <WaveformUnplayableMessage role="alert">
          <UnplayableIcon />
          This track can&apos;t be played in your browser.
        </WaveformUnplayableMessage>
      );
    }

    if (!showsWab) return scroller;

    return (
      <WabViewport>
        {scroller}
        <WabCanvas ref={canvasRef} />
      </WabViewport>
    );
  }
);

export default AudioWaveform;
